import { KeyboardArrowDown } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Skeleton,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useIntl } from 'react-intl';

import { useCareMatrix } from 'hooks/useCareMatrix';
import { getPatientAssessments } from 'services/user';

import { Error } from 'components/CareMatrixModule/components';
import { EmptyState } from 'components/CareMatrixModule/components/EmptyState';
import { Chart } from 'components/CareMatrixModule/tabs/summary/components/assessment/chart';

import * as S from './styles';

export function Assessment() {
  const intl = useIntl();
  const { patientCognitoId } = useCareMatrix();

  const {
    data: assessments,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['getPatientAssessments', patientCognitoId],
    queryFn: () => getPatientAssessments(patientCognitoId),
  });

  const lastAssessmentStatus = assessments?.last_assessment_status;
  const hasGad = !!assessments?.data.GAD.length;
  const hasPhq = !!assessments?.data.PHQ.length;
  const hasWho = !!assessments?.data.WHO.length;
  const emptyAssessments = !hasGad && !hasPhq && !hasWho;

  return (
    <Box>
      {!isError ? (
        <S.Wrapper>
          <Typography variant="h6" id="assessment-title">
            {intl.formatMessage({ id: 'CAREMATRIXCPTC.TITLEASSESSMENT.NAME' })}
          </Typography>

          <Box mt={1} display="flex" alignItems="center" gap={0.5}>
            {lastAssessmentStatus === 'todo' && (
              <>
                <S.AssessmentPending />
                <Typography variant="body2" color="error.light">
                  {intl.formatMessage({
                    id: 'CAREMATRIXCPTC.TITLEASSESSMENT.AVAILABLE',
                  })}
                </Typography>
              </>
            )}

            {lastAssessmentStatus === 'doing' && (
              <>
                <S.AssessmentNotFinished />
                <Typography variant="body2" color="info.main">
                  {intl.formatMessage({
                    id: 'CAREMATRIXCPTC.TITLEASSESSMENT.UNFINISHED',
                  })}
                </Typography>
              </>
            )}

            {lastAssessmentStatus === 'done' && (
              <>
                <S.AssessmentDone />
                <Typography variant="body2" color="success.main">
                  {intl.formatMessage({
                    id: 'CAREMATRIXCPTC.ASSESSMENT.ANSWERED',
                  })}
                </Typography>
              </>
            )}
          </Box>

          <Box mt={3}>
            {isLoading && (
              <Box display="flex" flexDirection="column" gap={1}>
                <Skeleton variant="rounded" width="386px" height="40px" />
                <Skeleton variant="rounded" width="386px" height="40px" />
                <Skeleton variant="rounded" width="386px" height="40px" />
              </Box>
            )}

            {!isLoading && (
              <>
                {hasGad && (
                  <Accordion>
                    <AccordionSummary expandIcon={<KeyboardArrowDown />}>
                      {intl.formatMessage({
                        id: 'ENDUSER.CLINICALASSESSMENT.GAD.LABEL',
                      })}
                    </AccordionSummary>
                    <AccordionDetails>
                      <Chart type="gad" results={assessments.data.GAD} />
                    </AccordionDetails>
                  </Accordion>
                )}

                {hasPhq && (
                  <Accordion>
                    <AccordionSummary expandIcon={<KeyboardArrowDown />}>
                      {intl.formatMessage({
                        id: 'ENDUSER.CLINICALASSESSMENT.PHQ.LABEL',
                      })}
                    </AccordionSummary>
                    <AccordionDetails>
                      <Chart type="phq" results={assessments.data.PHQ} />
                    </AccordionDetails>
                  </Accordion>
                )}

                {hasWho && (
                  <Accordion>
                    <AccordionSummary expandIcon={<KeyboardArrowDown />}>
                      {intl.formatMessage({
                        id: 'ENDUSER.CLINICALASSESSMENT.WHO.LABEL',
                      })}
                    </AccordionSummary>
                    <AccordionDetails>
                      <Chart type="who" results={assessments.data.WHO} />
                    </AccordionDetails>
                  </Accordion>
                )}
              </>
            )}

            {emptyAssessments && (
              <EmptyState
                text={intl.formatMessage({
                  id: 'CAREMATRIXCPTC.EMPTYSTATES.NOTRESPOND',
                })}
              />
            )}
          </Box>
        </S.Wrapper>
      ) : (
        <Error />
      )}
    </Box>
  );
}
